// Copied from AssetModal/Header/components/EditableAssetModalHeaderTitle.tsx

import { memo, useCallback, useEffect, useState } from 'react';

import { InlineInput, type InlineInputProps } from '@air/component-inline-input';
import { cn } from '@air/utils-tailwind';

export type EditableAssetTitleProps = Pick<InlineInputProps, 'onSubmit' | 'defaultValue' | 'validate' | 'className'>;

export const EditableAssetTitle = memo(({ onSubmit, defaultValue, validate, className }: EditableAssetTitleProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleInlineInputSubmit = useCallback(
    (newValue: string) => {
      setIsEditing(false);
      onSubmit?.(newValue);
    },
    [onSubmit],
  );

  const handleCancel = useCallback(() => {
    setValue(defaultValue);
    setIsEditing(false);
  }, [defaultValue]);

  return (
    <div className={cn('relative min-w-0', isEditing && 'min-w-[130px]', className)}>
      <button
        className="h-6 max-w-full truncate px-0.5 text-left font-14 font-semibold"
        data-testid="EDITABLE_ASSET_TITLE_BUTTON"
        onClick={handleEdit}
        type="button"
      >
        {value}
      </button>
      {isEditing && (
        <div className="absolute inset-x-0.5 inset-y-0 flex items-center">
          <InlineInput
            rows={1}
            allowNewLines={false}
            label="Asset name"
            onSubmit={handleInlineInputSubmit}
            onCancel={handleCancel}
            onChange={setValue}
            inputClassName="font-semibold border-none p-0 text-[inherit] md:leading-inherit leading-inherit tracking-[inherit] md:tracking-[inherit]"
            defaultValue={value}
            data-testid="ASSET_TITLE_INPUT"
            placeholder="Enter a name"
            validate={validate}
          />
        </div>
      )}
    </div>
  );
});

EditableAssetTitle.displayName = 'EditableAssetTitle';
